import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * @composable useActiveMenu
 * @description Evidenziazione reattiva del menu in base alla route corrente.
 * Persiste al reload. Match sul path: una foglia è attiva se il path corrente è '/' + slug o inizia
 * con '/' + slug + '/', per lo slug di ogni lingua (leaf.url è una chiave i18n o uno slug letterale)
 * e per i slug opzionali in leaf.activePaths (per pagine di dettaglio/creazione non collocate sotto
 * il prefisso della voce menu).
 *
 * @param {Array} menuItems - voci di primo livello di src/configs/menu.json (solo lettura)
 * @returns {{
 *   isLeafActive: (leaf: Object) => boolean,
 *   isItemActive: (item: Object) => boolean,
 *   activeSectionKey: import('vue').ComputedRef<number|null>,
 *   isDashboardActive: import('vue').ComputedRef<boolean>
 * }}
 * dove:
 *   - `isLeafActive(leaf)`: true se la route corrente matcha uno slug della foglia
 *   - `isItemActive(item)`: true se la voce o una sua discendente è attiva
 *   - `activeSectionKey`: indice della voce primaria attiva (0-based) o null se nessuna
 *   - `isDashboardActive`: true se la route è '/' (dashboard)
 */
export function useActiveMenu(menuItems) {
  const route = useRoute();
  const { t, te, availableLocales } = useI18n();

  function leafSlugs(leaf) {
    const slugs = availableLocales.map((locale) =>
      te(leaf.url, locale) ? t(leaf.url, {}, { locale }) : leaf.url
    );
    return slugs.concat(leaf.activePaths ?? []);
  }

  function matchesCurrentPath(slug) {
    return route.path === "/" + slug || route.path.startsWith("/" + slug + "/");
  }

  function isLeafActive(leaf) {
    return Boolean(leaf.url) && leafSlugs(leaf).some(matchesCurrentPath);
  }

  function isItemActive(item) {
    return isLeafActive(item) || (item.child ?? []).some(isItemActive);
  }

  const activeSectionKey = computed(() => {
    const index = menuItems.findIndex(isItemActive);
    return index === -1 ? null : index;
  });

  const isDashboardActive = computed(() => route.path === "/");

  return { isLeafActive, isItemActive, activeSectionKey, isDashboardActive };
}
